/**
 * Money in this system is always an integer number of cents in a named currency.
 * Never a float: 0.1 + 0.2 is not 0.3, and an invoice that is one cent out is an
 * invoice a finance department will reject.
 */

/** Rounds to a whole cent, half away from zero — the convention Stripe uses. */
export function roundCents(value: number): number {
  return value < 0 ? -Math.round(-value) : Math.round(value);
}

/**
 * A line total. Quantity is a count, so it must be a non-negative integer: a
 * fractional seat is a bug upstream, not something to silently round here.
 */
export function lineAmountCents(quantity: number, unitAmountCents: number): number {
  if (!Number.isInteger(quantity) || quantity < 0) {
    throw new RangeError(`quantity must be a non-negative integer, got ${quantity}`);
  }
  if (!Number.isInteger(unitAmountCents)) {
    throw new RangeError(`unitAmountCents must be an integer, got ${unitAmountCents}`);
  }
  return quantity * unitAmountCents;
}

/** Formats cents for display, e.g. 30000 -> "$300.00". Presentation only. */
export function formatCents(cents: number, currency = 'CAD', locale = 'en-CA'): string {
  return new Intl.NumberFormat(locale, { style: 'currency', currency }).format(cents / 100);
}

/**
 * An amount typed by a person, in dollars, to integer cents — or null when the
 * text is not unambiguously an amount.
 *
 * Bilingual on purpose: an operator in fr-CA types "1 250,50" and one in en-CA
 * types "1,250.50", and both mean the same cheque. Stripping everything but
 * digits and a dot — the obvious one-liner — reads the French "100,00" as
 * 10000 dollars, which is the error this exists to make impossible.
 *
 * - `,` or `.` may be the decimal mark; when both appear the last one is.
 * - Spaces, no-break spaces and narrow no-break spaces are thousands
 *   separators, as is the other mark when it groups digits in threes.
 * - A lone mark followed by exactly three digits is a thousands separator only
 *   when it is the locale's own grouping mark (`,` in English). Otherwise it is
 *   read as a decimal and refused for having three decimals, rather than
 *   guessed: a rejected amount costs a retype, a misread one costs money.
 * - Empty text, negative amounts, more than two decimals or any other
 *   character (a leading or trailing `$` aside) give null. Never zero by default.
 */
export function parseMoneyToCents(input: string, locale = 'en-CA'): number | null {
  // JavaScript's \s already covers the no-break and narrow no-break spaces
  // Intl puts between thousands in French, so one pass removes all of them.
  const text = input.replace(/\s/g, '').replace(/^\$/, '').replace(/\$$/, '');
  if (text === '' || !/^[0-9.,]+$/.test(text)) return null;

  const groupMark = locale.toLowerCase().startsWith('fr') ? null : ',';
  const lastComma = text.lastIndexOf(',');
  const lastDot = text.lastIndexOf('.');

  let whole: string;
  let fraction = '';
  if (lastComma === -1 && lastDot === -1) {
    whole = text;
  } else if (lastComma !== -1 && lastDot !== -1) {
    // Both marks: the last one is the decimal, the other must group thousands.
    const decimalAt = Math.max(lastComma, lastDot);
    const decimal = text[decimalAt] as string;
    const head = text.slice(0, decimalAt);
    if (head.includes(decimal)) return null;
    const grouped = ungroup(head, decimal === ',' ? '.' : ',');
    if (grouped === null) return null;
    whole = grouped;
    fraction = text.slice(decimalAt + 1);
  } else {
    const mark = lastComma !== -1 ? ',' : '.';
    const parts = text.split(mark);
    if (parts.length > 2) {
      // Repeated, so it can only be grouping: "1,234,567".
      const grouped = ungroup(text, mark);
      if (grouped === null) return null;
      whole = grouped;
    } else {
      const [head, tail] = parts as [string, string];
      if (tail.length === 3 && mark === groupMark && head.length > 0) {
        const grouped = ungroup(text, mark);
        if (grouped === null) return null;
        whole = grouped;
      } else {
        whole = head;
        fraction = tail;
      }
    }
  }

  if (whole === '') whole = '0';
  if (!/^[0-9]+$/.test(whole) || !/^[0-9]{0,2}$/.test(fraction)) return null;
  const cents = Number(whole) * 100 + Number(fraction.padEnd(2, '0'));
  return Number.isSafeInteger(cents) ? cents : null;
}

/** Removes a thousands mark, or null when it does not group digits in threes. */
function ungroup(text: string, mark: string): string | null {
  const groups = text.split(mark);
  const [first, ...rest] = groups as [string, ...string[]];
  if (!/^[0-9]{1,3}$/.test(first)) return null;
  if (!rest.every((g) => /^[0-9]{3}$/.test(g))) return null;
  return groups.join('');
}
